/* istanbul ignore file -- @preserve */
import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import {
  FormProvider as ReactHookFormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';

import Flex from '@decisiv/ui-components/lib/components/Flex';

export const FormProvider = ({
  initialValues,
  validationSchema,
  formConfig,
  children,
}) => {
  const methods = useForm({
    defaultValues: initialValues,
    resolver: validationSchema ? yupResolver(validationSchema) : undefined,
    mode: 'onChange',
    reValidateMode: 'onChange',
    ...formConfig,
  });

  return <ReactHookFormProvider {...methods}>{children}</ReactHookFormProvider>;
};

FormProvider.propTypes = {
  initialValues: PropTypes.shape({}).isRequired,
  validationSchema: PropTypes.shape({}),
  resolver: PropTypes.func,
  children: PropTypes.node.isRequired,
  formConfig: PropTypes.shape({}),
};

FormProvider.defaultProps = {
  validationSchema: undefined,
  resolver: undefined,
  formConfig: {},
};

const Form = forwardRef(({ onSubmit, children, ...props }, ref) => {
  const methods = useFormContext();

  const {
    setValue,
    setError,
    reset,
    handleSubmit,
    watch,
    clearErrors,
    formState,
  } = methods;

  const handleOnSubmit = handleSubmit(onSubmit);

  const setValues = useCallback(
    (values) => {
      Object.entries(values).forEach(([key, value]) => {
        setValue(key, value);
      });
    },
    [setValue],
  );

  useImperativeHandle(
    ref,
    () => ({
      setValue,
      setValues,
      setError,
      reset,
      submit: handleOnSubmit,
      watch,
      clearErrors,
      formState,
    }),
    [
      setValue,
      setValues,
      setError,
      reset,
      handleOnSubmit,
      watch,
      clearErrors,
      formState,
    ],
  );

  return (
    <Flex as="form" onSubmit={handleOnSubmit} {...props}>
      {children}
    </Flex>
  );
});

Form.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

Form.displayName = 'Form';

export { default as TextField } from './TextField';
export { default as Combobox } from './Combobox';
export { default as Select } from './Select';
export { default as CheckboxList } from './CheckboxList';
export { default as SelectDate } from './SelectDate';
export { default as RadioGroup } from './RadioGroup';
export { default as Checkbox } from './Checkbox';
export { default as CheckboxGroup } from './CheckboxGroup';

export default Form;

export function useFieldWatch(fieldNames) {
  const { control } = useFormContext();
  return useWatch({
    control,
    name: fieldNames,
  });
}
